// The RECORD panel of the hub (R229).
//
// The panel shows what one account has done: the standing it carries
// between seasons, the lifetime counters, the badges and the season
// chronicle. The subtitle YOU · LIFETIME names the split with RANKS, which
// holds the boards of all players; the ACHIEVEMENTS card carries one
// ▸ RANKS press to its board (R1737). The arcade ladder stands in the
// LADDER section of the arcade page and on RANKS, not here (R1948). It is
// the most nearly empty panel in the hub today, and it says so on every
// card rather than drawing a figure nobody wrote.
//
// **The section shell** (`../sectionshell.ts`, ui-map-interface.md §16.3
// and §16.16, card 03 of mockup round 092). Four sections: OVERVIEW keeps
// the whole record in the first screen (STANDING, the newest badges and
// the chronicle teaser as a grid of cards, R1744, R1948);
// ACHIEVEMENTS holds the shelf, and a category opens as a sub-page in the
// pane under ‹ ACHIEVEMENTS; CHRONICLE holds the book; STATISTICS holds
// the lifetime counters. Each rail row and tile states a live summary
// from the reads below, and none where the read has not answered. The
// companion column is WITHIN REACH on OVERVIEW and STATISTICS (the
// achievements nearest their next step, from the lifetime totals of the
// record), YOUR PICK on ACHIEVEMENTS and VOLUMES on CHRONICLE. Under the
// room for the column it folds after the page, and a telephone shows
// WITHIN REACH under the tiles.
//
// What it reads:
//
//   `/api/stats/<id>`      the lifetime counters. The table is real and no
//                          generator fills it yet, so `updatedAt` is
//                          absent and every counter is zero.
//   `/api/achievements/me` the account's own grades, recognitions and
//                          totals. A meta server with no progress block
//                          answers 503 and the card says so.
//   `/api/chronicle/me/…`  the season chronicle (chronicle.md §9): the
//                          volume list for both states of the card, and
//                          the pages of the newest volume with the key.
//                          The book itself is `../chronicle/book.ts`.
//
// The account, its Honour and its placements (R223) come from `ctx.me()`,
// which the shell reads.
//
// What is honestly empty today:
//
//   - A statistics card with no `updatedAt` says nothing has been recorded.
//     It must not draw zeros, because a zero counter reads as a result.
//   - The achievement record is a route and not a guess. A 503 draws the
//     failed state; it never draws a zero, because a zero nobody measured
//     reads as a result (achievements.md §8).
//   - A chronicle with no volume gives an empty state. An account without
//     the key gets a faded description of the card, the count of the days
//     written, and a full-colour way to the shop.
//   - The panel draws no bar chart, no battle report and no log. The
//     mockup drew all three from invented figures, and no route answers
//     them.
//
// The badge dot (ui-map-interface.md §16.4): `dot()` is on while the record
// holds a grade newer than the stamp this browser holds, and opening the
// section is what clears it. The dot and the pane read ONE cache cell
// (R1047), so the shell's tick costs one `GET /api/achievements/me` per
// cell life and never a request per redraw. A render of the ready grid
// writes the stamp one time per visit.

import { sellsNothing } from '../../../platform/platform';
import { button } from '../../choice/choice';
import {
  getMyAchievements, getStats, KEY_CHRONICLE, KEY_EVENT,
  type AccountAchievements, type AccountStats, type AchievementScope,
} from '../../../net/api';
import { MODE_CORE } from '../../../gen/balance';
import {
  formatAgo, groupDigits, hasKey, loadSeenAt, saveSeenAt, TTL_ON_OPEN,
} from '../hubstate';
import { hubRecord as S, achievements as A, chronicle as C } from '../../../gen/strings';
import { wordOf } from '../../../locale/wordof';
// The card's row count rides presentation.toml [presentation.chrome].
import {
  ACHIEVEMENT_LOOK, CHROME_CHRONICLE_SHOWN, HUB_SECTIONS, type AchievementRow,
} from '../../../gen/presentation';
import {
  categoryView, gradeCount, gradeMap, newestGradeAt, nextThreshold, pointsOf, rowByCode, zoneOf,
  rowNext, rowsOfCategory, rowValue, visibleCategories,
} from '../../achievements';
import { badgeCanvas } from '../../../render/badges';
import { gradeName, nameOf } from '../../unlock';
import {
  empty, esc, figLine, h1, lede, pending, ctlRow, plain, readBoard, rows,
  section, tierTag, type PageKey, type Panel, type PanelContext, type RanksBoard, type Row,
} from '../panel';
import {
  ChronicleBook, dayViews, lineHtml, ownVoices, scopeOf, volumeCell, volumesCell,
} from '../chronicle/book';
import {
  SectionNav, cardGrid, shellHtml, shellPress, type Companion, type ShellPage, type ShellSection,
} from '../sectionshell';

/** The four sections of RECORD, in the rail order (§16.3). The ids are
 *  the address words: `?tab=record&section=chronicle`, and the old
 *  `?tab=record&page=chronicle` of R1684 names the same section. */
export const RECORD_SECTIONS = ['overview', 'achievements', 'chronicle', 'statistics'] as const;
type RecordSection = (typeof RECORD_SECTIONS)[number];

/** The press of a rail row and of a tile, and ‹ RECORD of a narrow page. */
const GO = 'rec-sec';
const BACK = 'rec-back';
const SHELL = { go: GO, back: BACK };

/** One achievement within reach: a lifetime row, the step it holds, the
 *  share of its next step and the two figures. */
export interface ReachRow {
  row: AchievementRow;
  grade: number;
  share: number;
  now: string;
  next: string;
}

/**
 * The achievements nearest their next step (the WITHIN REACH column).
 *
 * A lifetime row of a category the reader may see, with a metric the
 * record's lifetime totals carry and a next step it has not reached,
 * stands with the share of that step it holds. The rows sort by the share,
 * the largest first, and the code breaks a tie, so two renders agree.
 * A journey row counts in a season scope and a recognition has no step,
 * so neither stands here.
 */
export function withinReach(mine: AccountAchievements, entitled: boolean, count: number): ReachRow[] {
  const held = gradeMap(mine);
  const out: ReachRow[] = [];
  for (const cat of visibleCategories(entitled)) {
    for (const row of rowsOfCategory(cat)) {
      if (row.kind !== 'lifetime' || row.metric === '') continue;
      const value = mine.totals[row.metric];
      const grade = held.get(row.code) ?? 0;
      const next = nextThreshold(row, grade);
      if (value === undefined || next === undefined || next <= 0 || value >= next) continue;
      out.push({
        row, grade, share: Math.max(0, value / next),
        now: rowValue(row, mine.totals, groupDigits) ?? '',
        next: rowNext(row, grade, groupDigits) ?? '',
      });
    }
  }
  out.sort((a, b) => b.share - a.share || byCode(a.row.code, b.row.code));
  return out.slice(0, count);
}

/** The order of two codes, so a tie sorts the same on every render. */
function byCode(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** The newest grades of the record, newest first: a code and the step. */
export function newestGrades(mine: AccountAchievements, count: number): { code: string; grade: number }[] {
  return [...mine.grades]
    .filter((g) => g.grade > 0)
    .sort((a, b) => b.reachedAt - a.reachedAt || byCode(a.code, b.code))
    .slice(0, count)
    .map((g) => ({ code: g.code, grade: g.grade }));
}

/** How many grades and recognitions the record reached after `since`. */
function newSince(mine: AccountAchievements, since: number): number {
  return mine.grades.filter((g) => g.reachedAt > since).length
    + mine.recognitions.filter((r) => r.grantedAt > since).length;
}

/** A bar filled to `share`, 0 to 1, of one achievement or one category. */
function bar(share: number): string {
  return `<span class="bar"><i style="width:${Math.round(share * 100)}%"></i></span>`;
}

/** The ▸ RANKS press of a card: it opens RANKS on the board `board`
 *  (R1737). */
function toRanks(board: RanksBoard): string {
  return ctlRow(button(S.to_ranks, { act: 'ranks', data: { board } }, { variant: 'primary' }));
}

/** The tile's badge, in CSS px. The grid is a picture of a shelf, so a
 *  tile's badge is the catalog's own size and never a thumbnail. */
const TILE_PX = ACHIEVEMENT_LOOK.badgePx;
/** A row's badge on the category page, which is a list and not a shelf. */
const ROW_PX = 32;

/** Where the browser remembers the newest grade it has shown the dot for.
 *  It is a per-viewer convenience, the way the NEWS stamp is: a store that
 *  refuses just brings the dot back on the next visit. */
export const ACH_SEEN_KEY = 'aquaphobia.hub.achievementsSeenAt';

/** One achievement badge, mounted by `wire` after the pane is written. A
 *  canvas cannot survive an innerHTML rewrite, so the markup carries the
 *  question and `wire` paints the answer. */
function badgeSlot(category: string, grade: number, px: number): string {
  return `<canvas class="badge" data-badge="${esc(category)}"`
    + ` data-grade="${grade}" data-px="${px}"></canvas>`;
}

/** The name of one category. A category the locale does not name keeps
 *  its id, so no tile goes blank. */
function categoryName(id: string): string {
  return wordOf(A, `category_${id}`, id.toUpperCase());
}

export function createRecordPanel(ctx: PanelContext): Panel {
  /** The open section and the section the address named (§16.16). */
  const nav = new SectionNav({ ids: () => RECORD_SECTIONS, first: () => 'overview' });
  /** The category page open inside ACHIEVEMENTS, or '' for the shelf. */
  let openCategory = '';
  /** The achievement the reader picked on a category page, or '' for the
   *  nearest one: the YOUR PICK column shows it. */
  let picked = '';
  /** The seen stamp as the panel opened: the ACHIEVEMENTS row counts the
   *  grades reached after it, for the whole visit. */
  let seenAtOpen = 0;
  const book = new ChronicleBook(ctx);
  /** The stamp this panel last wrote, so a redraw four times a second
   *  writes to the store once and not four times. */
  let stamped = -1;

  /**
   * Marks the record seen, which is what clears the dot.
   *
   * Opening the panel is the gesture, the way it is for NEWS. The write
   * happens where the answer is in hand rather than in `open`, because the
   * first open is what STARTS the read and there is nothing to stamp yet.
   */
  function markSeen(mine: AccountAchievements): void {
    const at = newestGradeAt(mine);
    if (at === stamped) return;
    stamped = at;
    saveSeenAt(ACH_SEEN_KEY, at);
  }

  /** The standing the account carries between seasons. The three Honour
   *  ladders are three ladders, so the card prints them apart and never
   *  adds them up. Clan Honour is what a Clan War pays (R1512). The three
   *  rows share one label form, `HONOUR · <LADDER>`, and the card carries
   *  no count of live placements and no lede (R1561). The EVENT and the
   *  CLAN row read in the ink of their PLAY tile (R2151). */
  function standingCard(): string {
    const me = ctx.me();
    const played = stats().value?.seasonsPlayed;
    const seasons = played === undefined ? '—' : String(played);
    const list: Row[] = [
      [S.row_honour_core, S.honour_value({ n: groupDigits(me.account.honourCore) }), 'acc'],
      [S.row_honour_event, S.honour_value({ n: groupDigits(me.account.honourEvent) }), 'event'],
      [S.row_honour_clanwar, S.honour_value({ n: groupDigits(me.account.honourClanwar ?? 0) }), 'clanwar'],
      [S.row_seasons_played, seasons],
    ];
    return section(S.card_standing, me.account.kind.toUpperCase(), rows(list));
  }

  /** The account's lifetime counters. Both cards that print one read this
   *  one cell. */
  function stats() {
    const id = ctx.me().account.id;
    return ctx.data.cell<AccountStats>(`stats:${id}`, TTL_ON_OPEN, () => getStats(id));
  }

  /** The lifetime counters. */
  function statsCard(): string {
    const cell = stats();
    const s = cell.value;
    if (s === undefined) return plain(figLine(S.stats_more) + pending(cell));
    if (s.updatedAt === undefined) {
      // Every counter is zero and nothing wrote them. A zero that nobody
      // measured is not a result, so the card prints none of them.
      return plain(figLine(S.stats_more) +
        empty(S.nothing_recorded, S.stats_empty_why, { label: S.stats_empty_press, tab: 'play' }));
    }
    const list: Row[] = [
      [S.row_best_score, groupDigits(s.bestScore)],
      [S.row_hex_taken, groupDigits(s.cellsTaken), 'good'],
      [S.row_fleets_lost, groupDigits(s.fleetsLost), 'bad'],
      [S.row_seasons_played, String(s.seasonsPlayed)],
    ];
    return plain(figLine(formatAgo(ctx.now(), s.updatedAt)) + rows(list));
  }

  /**
   * The season scope the record's own `season` half belongs to (R951).
   *
   * The hub has no world open, so it reads the account's active CORE
   * placement: a core season is the one a journey row counts in, and an
   * arcade seat holds no season at all. The list already holds live seats
   * alone — the meta server drops a retired world and a placement of an
   * earlier season — so the test is the mode and a named season.
   *
   * An account with no such placement names no scope, and the call then
   * answers the lifetime half alone.
   */
  function scope(): AchievementScope | undefined {
    const p = ctx.me().placements.find(
      (x) => x.mode === MODE_CORE && x.worldId > 0 && x.season > 0);
    return p ? { mode: p.mode, world: p.worldId, season: p.season } : undefined;
  }

  /** The account's own record. Every achievement surface of the hub reads
   *  this one cell, the badge dot as well (R1047). The scope rides the key,
   *  so a season roll asks again rather than showing the old season's
   *  values; the grades and the recognitions the dot reads are lifetime
   *  values and stand the same in every scope. */
  function record() {
    const s = scope();
    const key = s ? `achievements:me:${s.mode}:${s.world}:${s.season}` : 'achievements:me';
    return ctx.data.cell<AccountAchievements>(key, TTL_ON_OPEN, () => getMyAchievements(s));
  }

  /**
   * The achievements card: a grid of category tiles (mockup round
   * forty-four, card 11).
   *
   * One tile per category, the badge at the deepest grade the account holds
   * in it, the category name, how many grades it has of how many, and a bar
   * that fills by the same two figures. A tile the account has nothing in
   * still draws its badge, quietly, so the shelf reads as a shelf.
   *
   * A gated category draws for an entitled account ALONE, and the tile is
   * absent rather than greyed: a row that says "you may not see this" is
   * itself a leak of what is behind it (§8).
   */
  function badgeCard(): string {
    const cell = record();
    const mine = cell.value;
    // A meta server with no progress block answers 503. That is an honest
    // "not available", and it is never drawn as a zero.
    if (mine === undefined) return plain(pending(cell) + toRanks('achievements'));
    markSeen(mine);
    const more = `${A.record_points({ points: groupDigits(pointsOf(mine)) })}`
      + ` · ${A.record_grades({ count: groupDigits(gradeCount(mine)) })}`;
    let body = '<div class="badgegrid">';
    for (const id of visibleCategories(entitled())) {
      const v = categoryView(id, mine);
      body += `<button type="button" class="btile" data-act="achcat" data-cat="${esc(id)}">`
        + badgeSlot(id, v.best, TILE_PX)
        + `<span class="n">${esc(categoryName(id))}</span>`
        + `<span class="c">${esc(A.category_progress({ done: v.reached, total: v.possible }))}</span>`
        + bar(v.possible === 0 ? 0 : v.reached / v.possible)
        + '</button>';
    }
    body += '</div>';
    if (gradeCount(mine) === 0) body += lede(A.record_empty);
    return plain(figLine(more) + body + toRanks('achievements'));
  }

  /**
   * One category, as a sub-page of ACHIEVEMENTS inside the pane (§16.16).
   *
   * The ‹ ACHIEVEMENTS bar at the top left takes the reader back to the
   * shelf, the one back form of a hub sub-page (R1751), and names the
   * category in grey beside it (R1780); the rail stays. Every row of the
   * category stands whether the account holds it or not, because a ladder
   * a player cannot see is a ladder nobody climbs. A press on a row picks
   * it for the YOUR PICK column.
   */
  function categoryPage(id: string): string {
    const cell = record();
    const mine = cell.value;
    if (mine === undefined) return plain(pending(cell));
    const held = gradeMap(mine);
    const awards = new Map(mine.recognitions.map((r) => [r.code, r]));
    const v = categoryView(id, mine);
    let body = '';
    for (const row of rowsOfCategory(id)) {
      body += achievementRow(row, held.get(row.code) ?? 0, awards.get(row.code), mine);
    }
    if (body === '') body = empty(A.record_empty);
    return plain(figLine(A.category_progress({ done: v.reached, total: v.possible })) + body);
  }

  /** One row of a category page: the badge, the words and the figures. */
  function achievementRow(
    row: AchievementRow,
    grade: number,
    award: AccountAchievements['recognitions'][number] | undefined,
    mine: AccountAchievements,
  ): string {
    const desc = wordOf(A, `${row.code}_desc`, '');
    const recognition = row.kind === 'recognition';
    const badge = recognition
      ? badgeSlot('recognition', award ? 1 : 0, ROW_PX)
      : badgeSlot(row.category, zoneOf(row, grade), ROW_PX);
    // A recognition states the day it was granted and never the reason: the
    // reason is an operator's note and belongs to the operator (§6).
    const value = recognition
      ? (award ? A.granted_on({ when: formatAgo(ctx.now(), award.grantedAt) }) : A.grade_none)
      : grade > 0 ? gradeName(zoneOf(row, grade)) : A.grade_none;
    // Both figures go through the row's own unit: a `sec` metric reads as
    // a span of the player's time and never as raw seconds (R949). The
    // hub's own grouper keeps the column measured in its monospaced face.
    const now = rowValue(row, mine.totals, groupDigits);
    const next = rowNext(row, grade, groupDigits);
    const foot = recognition
      ? (award ? A.recognition_label : '')
      : [
        now === undefined ? '' : A.row_value({ value: now }),
        next === undefined ? A.row_max : A.next_threshold({ value: next }),
      ].filter((t) => t !== '').join(' · ');
    // A press on the row picks it for the YOUR PICK column (§16.3).
    const on = row.code === pickedCode(mine);
    return `<button type="button" class="achrow${grade > 0 || award ? ' held' : ''}${on ? ' on' : ''}"`
      + ` data-act="achpick" data-code="${esc(row.code)}"${on ? ' aria-pressed="true"' : ''}>`
      + badge
      + `<span class="t"><span class="n">${esc(nameOf(row.code))}</span>`
      + `<span class="d">${esc(desc)}</span>`
      + (foot === '' ? '' : `<span class="f">${esc(foot)}</span>`)
      + '</span>'
      + `<span class="g">${esc(value)}</span></button>`;
  }

  /**
   * The season chronicle (chronicle.md §9): its tier tag and its content,
   * which OVERVIEW draws as a section and CHRONICLE without the key as the
   * page (R1945). The volume list needs no key, so both states read it.
   *
   * With the key, the card shows the first lines of the newest day page and
   * a press that opens the book. Without the key, the content fades and the
   * way to the shop stays in full colour (R229). The card also counts the
   * days the game already wrote for the live volume, because the facts
   * exist without the key (R1167). No volume means no count.
   */
  function chronicleParts(): { tag: string; inner: string } {
    const me = ctx.me();
    const held = hasKey(me, KEY_CHRONICLE);
    const tag = tierTag('supporter', held, me);
    const vc = volumesCell(ctx);
    const volumes = vc.value;
    if (!held) {
      const writing = volumes?.find((v) => v.live) ?? volumes?.[0];
      const body = '<div class="faded">'
        + lede(S.chronicle_lede)
        + '</div>'
        + (writing === undefined ? '' : lede(S.chronicle_writing({ days: writing.days })))
        + (sellsNothing() ? '' : ctlRow(button(S.see_the_shop, { act: 'shop' }, { variant: 'gold' })));
      return { tag, inner: body };
    }
    if (volumes === undefined) return { tag, inner: pending(vc) };
    const newest = volumes[0];
    if (newest === undefined) {
      // A held key with no volume has nothing to unlock, so the card wears
      // no tier tag (R1742). The STATISTICS card holds the press to PLAY.
      return { tag: '', inner: empty(C.no_volume, C.no_volume_why) };
    }
    // The tier tag stands after the name of the section, or at the right
    // of the page title (R1547, R1945), so the volume label is the first
    // line of the body.
    const label = lede(C.volume_label({ season: newest.season, world: newest.worldName }));
    const open = ctlRow(button(C.open_book, { act: 'chrbook' }, { variant: 'primary' }));
    const pc = volumeCell(ctx, newest.world, newest.season);
    const pages = pc.value;
    if (pages === undefined) return { tag, inner: label + pending(pc) + open };
    const day = dayViews(pages, scopeOf(ctx, pages), ownVoices(ctx, pages))[0];
    const teaser = day === undefined ? empty(C.no_page)
      : `<div class="chron"><div class="cs">${esc(day.header)}</div>`
        + day.lines.slice(0, CHROME_CHRONICLE_SHOWN).map(lineHtml).join('') + '</div>';
    return { tag, inner: label + teaser + open };
  }

  /** The chronicle as one section of OVERVIEW, the tier tag after its
   *  name (R1945). */
  function chronicleCard(): string {
    const c = chronicleParts();
    return section(S.card_chronicle, '', c.inner, '', c.tag);
  }

  // --- the section shell (§16.3, §16.16) ------------------------------------

  /** True where the account may see the gated categories. */
  function entitled(): boolean {
    return hasKey(ctx.me(), KEY_EVENT);
  }

  /** The OVERVIEW summary: the core Honour. */
  function overviewSum(): string {
    return S.sum_overview_honour({ honour: groupDigits(ctx.me().account.honourCore) });
  }

  /** The ACHIEVEMENTS summary: the grades held of the grades the visible
   *  categories hold. None until the record answers. */
  function achievementsSum(): string {
    const mine = record().value;
    if (mine === undefined) return '';
    let held = 0;
    let total = 0;
    for (const id of visibleCategories(entitled())) {
      const v = categoryView(id, mine);
      held += v.reached;
      total += v.possible;
    }
    return S.sum_achievements({ held: groupDigits(held), total: groupDigits(total) });
  }

  /** The CHRONICLE summary: the newest volume and its days written. */
  function chronicleSum(): string {
    const list = volumesCell(ctx).value;
    if (list === undefined) return '';
    const newest = list[0];
    if (newest === undefined) return S.sum_chronicle_none;
    return S.sum_chronicle({ season: newest.season, days: newest.days });
  }

  /** The STATISTICS summary: the seasons played and the best score, or
   *  the word that nothing is recorded. */
  function statisticsSum(): string {
    const st = stats().value;
    if (st === undefined) return '';
    if (st.updatedAt === undefined) return S.sum_statistics_none;
    return S.sum_statistics({ seasons: st.seasonsPlayed, score: groupDigits(st.bestScore) });
  }

  /** The four sections as the rail and the tiles draw them. */
  function sections(): ShellSection[] {
    const mine = record().value;
    const fresh = mine === undefined ? 0 : newSince(mine, seenAtOpen);
    return [
      { id: 'overview', glyph: '✦', name: S.sec_overview, sum: overviewSum() },
      { id: 'achievements', glyph: '◈', name: S.sec_achievements, sum: achievementsSum(), badge: fresh },
      { id: 'chronicle', glyph: '▤', name: S.sec_chronicle, sum: chronicleSum() },
      { id: 'statistics', glyph: '▦', name: S.sec_statistics, sum: statisticsSum() },
    ];
  }

  /** One line of a list of badges: the badge, the name and the grade. */
  function badgeLine(code: string, grade: number): string {
    const row = rowByCode(code);
    const zone = zoneOf(row, grade);
    return `<div class="reachrow"><div class="rh">${badgeSlot(row?.category ?? code, zone, ROW_PX)}`
      + `<span class="rn">${esc(nameOf(code))}</span><b>${esc(gradeName(zone))}</b></div></div>`;
  }

  /**
   * WITHIN REACH: the achievements nearest their next step, each with its
   * bar and its two figures. Where the record holds no such step, the
   * newest badges stand instead, under a line that says so. `play` adds
   * the ▸ PLAY press, the way to the next step. An empty record carries
   * the press always, because its line asks the reader to play (R2151).
   */
  function reachHtml(play: boolean): string {
    const cell = record();
    const mine = cell.value;
    if (mine === undefined) return pending(cell);
    const list = withinReach(mine, entitled(), HUB_SECTIONS.recordReachRows);
    const go = ctlRow(button(S.reach_play, { act: 'tab', data: { tab: 'play' } }, { variant: 'primary' }));
    const press = play ? go : '';
    if (list.length === 0) {
      const newest = newestGrades(mine, HUB_SECTIONS.recordNewestBadges);
      if (newest.length === 0) return lede(A.record_empty) + go;
      return lede(S.reach_none) + newest.map((g) => badgeLine(g.code, g.grade)).join('') + press;
    }
    return list.map((r) => `<div class="reachrow"><div class="rh"><span class="rn">${esc(nameOf(r.row.code))}</span>`
      + `<b>${esc(S.reach_value({ now: r.now, next: r.next }))}</b></div>`
      + `<div class="rc">${esc(categoryName(r.row.category))}</div>${bar(r.share)}</div>`).join('') + press;
  }

  /** The code the YOUR PICK column shows: the picked row, else the row
   *  nearest its next step, else ''. */
  function pickedCode(mine: AccountAchievements): string {
    if (picked !== '' && rowByCode(picked) !== null) return picked;
    return withinReach(mine, entitled(), 1)[0]?.row.code ?? '';
  }

  /** YOUR PICK: the picked achievement at the size of the shelf, its
   *  grade, its sentence, its bar and its figures, and ▸ RANKS. */
  function pickHtml(): string {
    const cell = record();
    const mine = cell.value;
    if (mine === undefined) return pending(cell);
    const code = pickedCode(mine);
    const row = rowByCode(code);
    if (row === null) return lede(S.pick_hint);
    const grade = gradeMap(mine).get(code) ?? 0;
    const zone = zoneOf(row, grade);
    const now = rowValue(row, mine.totals, groupDigits);
    const next = rowNext(row, grade, groupDigits);
    const target = nextThreshold(row, grade);
    const value = row.metric === '' ? undefined : mine.totals[row.metric];
    const share = target === undefined || target <= 0 || value === undefined ? 1 : Math.min(1, value / target);
    const progress = next === undefined ? A.row_max
      : now === undefined ? A.next_threshold({ value: next }) : S.reach_value({ now, next });
    const desc = wordOf(A, `${code}_desc`, '');
    return `<div class="pickhead">${badgeSlot(row.category, zone, TILE_PX)}<span class="t">`
      + `<span class="n">${esc(nameOf(code))}</span>`
      + `<span class="c">${esc(categoryName(row.category))} · ${esc(zone > 0 ? gradeName(zone) : A.grade_none)}</span>`
      + '</span></div>'
      + (desc === '' ? '' : lede(desc))
      + bar(share)
      + rows([[S.row_progress, progress]]) + toRanks('achievements');
  }

  /** VOLUMES: the volume list of the book, the open one marked, in a box
   *  that scrolls past a fixed count of rows (R1920). A press opens a
   *  volume in the book. */
  function volumesHtml(): string {
    const cell = volumesCell(ctx);
    const list = cell.value;
    if (list === undefined) return pending(cell);
    if (list.length === 0) return lede(C.no_volume);
    return book.list(list);
  }

  /** The NEWEST BADGES card of OVERVIEW: the newest grades of the record
   *  and the press to ACHIEVEMENTS. */
  function newestCard(): string {
    const cell = record();
    const mine = cell.value;
    const more = ctlRow(button(S.to_achievements, { act: GO, data: { id: 'achievements' } }, { variant: 'primary' }));
    if (mine === undefined) return section(S.card_newest, '', pending(cell) + more);
    const list = newestGrades(mine, HUB_SECTIONS.recordNewestBadges);
    const body = list.length === 0 ? empty(S.newest_none)
      : list.map((g) => badgeLine(g.code, g.grade)).join('');
    return section(S.card_newest, achievementsSum(), body + more);
  }

  /** WITHIN REACH as a companion: the column, and after the page under
   *  the room for it. */
  function reach(): Companion {
    return { label: S.reach_label, html: reachHtml(true), fold: 'below' };
  }

  /** The page of one section. */
  function sectionPage(id: RecordSection, wide: boolean): ShellPage {
    switch (id) {
      case 'overview':
        return {
          body: cardGrid(standingCard(), newestCard(), chronicleCard()),
          companion: reach(),
        };
      case 'achievements': {
        const pick: Companion = { label: S.pick_label, html: pickHtml(), fold: 'below' };
        if (openCategory !== '') {
          return {
            body: categoryPage(openCategory),
            sub: { name: categoryName(openCategory), back: 'achback' },
            companion: pick,
          };
        }
        return { body: badgeCard(), companion: pick };
      }
      case 'chronicle': {
        // The book stands for the key alone. A wide pane lists the volumes
        // in the VOLUMES column, so the book draws no bar of its own; a
        // telephone keeps the bar at the foot of the pane.
        if (!hasKey(ctx.me(), KEY_CHRONICLE)) {
          const c = chronicleParts();
          return { body: plain(c.inner), mark: c.tag };
        }
        return {
          body: book.html({ bar: !wide }),
          companion: wide ? { label: S.volumes_label, html: volumesHtml(), fold: 'below' } : undefined,
        };
      }
      case 'statistics':
        return { body: statsCard(), companion: reach() };
    }
  }

  /** A section opened, or the tiles (null). A new visit of CHRONICLE
   *  opens the book on the newest volume, as OPEN THE BOOK did; a new
   *  visit of ACHIEVEMENTS and the tiles drop the open category. */
  function moved(to: RecordSection | null): void {
    if (to === 'chronicle') book.reset();
    if (to === null || to === 'achievements') openCategory = '';
  }

  return {
    key: 'record',

    open(): void {
      // A second visit must clear the dot again, so the guard is dropped
      // here and the next draw writes the stamp. The stamp as it stands
      // now is what the ACHIEVEMENTS count reads for the whole visit.
      stamped = -1;
      seenAtOpen = loadSeenAt(ACH_SEEN_KEY);
      openCategory = '';
      picked = '';
      nav.open();
    },

    close(): void {
      nav.close();
    },

    // The way back from a replay link opens the book on the live volume
    // of the world the film played in (R1684): the CHRONICLE section.
    openPage(p: PageKey, world = 0): void {
      if (!nav.ask(p)) return;
      if (p === 'chronicle') {
        book.reset();
        book.openOn(world);
      }
    },

    openSection(word: string): void {
      nav.ask(word);
    },

    html(): string {
      // Opening the panel is the gesture that clears the dot, the way it
      // is for NEWS: the stamp is written where the answer is in hand.
      const mine = record().value;
      if (mine !== undefined) markSeen(mine);
      const wide = ctx.wide();
      const open = nav.shown(wide);
      return shellHtml({
        title: S.title,
        heading: h1(S.title, S.title_sub),
        go: GO,
        back: BACK,
        sections: sections(),
        open,
        wide,
        page: sectionPage(open ?? 'overview', wide),
        rootFoot: section(S.reach_label, '', reachHtml(false)),
      });
    },

    wire(root: HTMLElement): void {
      nav.scroll(root);
      book.wire(root);
      // The pane was rewritten, so every badge canvas is gone. A canvas
      // cannot survive an innerHTML rewrite and cannot stand in two places,
      // so the markup carries the question and the bake answers it; the
      // bitmap itself is cached per (category, grade, px).
      for (const slot of root.querySelectorAll<HTMLCanvasElement>('canvas[data-badge]')) {
        const px = Number.parseInt(slot.dataset.px ?? String(TILE_PX), 10);
        const grade = Number.parseInt(slot.dataset.grade ?? '0', 10);
        const painted = badgeCanvas(slot.dataset.badge ?? '', grade, px);
        slot.replaceWith(painted);
        painted.className = 'badge';
      }
    },

    dot(): boolean {
      // The dot lights when the record holds a grade newer than the last
      // one this browser was shown. It reads the SAME cell the pane reads,
      // the way the NEWS dot does (R1047): a cell has a life of five
      // minutes, so the shell's tick costs one `GET /api/achievements/me`
      // per cell life and never a request per redraw. A dot that only
      // peeked started no load, so it could light in no state the reader
      // ever saw.
      const cell = record();
      return cell.value !== undefined
        && newestGradeAt(cell.value) > loadSeenAt(ACH_SEEN_KEY);
    },

    act(action: string, el: HTMLElement): void {
      if (shellPress(nav, ctx, SHELL, action, el, moved)) return;
      switch (action) {
        case 'shop':
          ctx.play('sfx-menu-press');
          ctx.go('shop');
          return;
        case 'ranks':
          ctx.play('sfx-menu-press');
          ctx.go('ranks', readBoard(el.dataset.board ?? ''));
          return;
        case 'achcat':
          ctx.play('sfx-menu-press');
          // The shelf keeps the place the reader left (R1919).
          if (openCategory === '') nav.enter();
          openCategory = el.dataset.cat ?? '';
          ctx.refresh();
          return;
        case 'achback':
          ctx.play('sfx-menu-back');
          nav.leave();
          openCategory = '';
          ctx.refresh();
          return;
        case 'achpick':
          ctx.play('sfx-menu-press');
          picked = el.dataset.code ?? '';
          ctx.refresh();
          return;
        case 'chrbook':
          // OPEN THE BOOK of the teaser opens the CHRONICLE section.
          if (!nav.go('chronicle', ctx.wide())) return;
          moved('chronicle');
          ctx.play('sfx-menu-tab');
          ctx.refresh();
          return;
        default:
          // The book takes its own presses: a volume, the steps and the
          // list press of the bar, the share switch and COPY LINK.
          book.act(action, el);
      }
    },
  };
}
